import React, { useEffect } from 'react';

import AppSettings from '../../constants';
import { useRole } from '../hooks/useRole';
import { Box } from '../ui/Box';

import { CommandDialog } from './components/CommandDialog';
import { CommandFooter } from './components/CommandFooter';
import { CommandInput } from './components/CommandInput';
import { CommandListRouter } from './components/CommandListRouter';
import { CommandListNestedRouterPathEnum } from './components/commandRoutes';
import { useShortcutMenu } from './hooks/useShortcutMenu';

export const ShortcutMenu = () => {
    const role = useRole();
    const {
        open,
        setOpen,
        search,
        setSearch,
        selectedValue,
        setSelectedValue,
        pages,
        setPages,
        groupContextActionType,
        setGroupContextActionType,
    } = useShortcutMenu();

    // Toggle the menu when ⌘K is pressed
    useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((open) => !open);
                setSearch('');
                setSelectedValue(undefined);
                setPages([]);
                setGroupContextActionType(null);
            }
        };

        document.addEventListener('keydown', down);
        return () => document.removeEventListener('keydown', down);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setSearch('');
        setSelectedValue(undefined);
    }, [pages, setSearch, setSelectedValue]);

    // Disable for non-admins in production
    if (AppSettings.ENV === 'production' && !role.isSuperAdmin()) {
        return null;
    }

    return (
        <CommandDialog
            key={pages.length}
            loop
            open={open}
            value={selectedValue}
            onValueChange={setSelectedValue}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                if (!nextOpen) {
                    setSearch('');
                    setSelectedValue(undefined);
                    setPages([]);
                    setGroupContextActionType(null);
                }
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                // Escape goes to previous page
                // Backspace goes to previous page when search is empty
                if (e.key === 'Backspace' && !search) {
                    e.preventDefault();
                    if (pages[pages.length - 1] === CommandListNestedRouterPathEnum.CONTEXT && groupContextActionType) {
                        setGroupContextActionType(null);
                        return;
                    }
                    setPages((pages) => pages.slice(0, -1));
                }

                if (e.key === 'Escape') {
                    e.preventDefault();
                    setOpen(false);
                    setSearch('');
                    setPages([]);
                    setGroupContextActionType(null);
                }
            }}
            shouldFilter={
                pages.length === 0 ||
                pages[pages.length - 1] === CommandListNestedRouterPathEnum.CREATE ||
                pages[pages.length - 1] === CommandListNestedRouterPathEnum.NAVIGATE ||
                (pages[pages.length - 1] === CommandListNestedRouterPathEnum.CONTEXT && !groupContextActionType)
            }
        >
            <Box padding={16} borderBottom="1px solid" borderColor="border.subtle">
                <CommandInput search={search} onChange={setSearch} />
            </Box>

            <Box paddingX={8}>
                <CommandListRouter />
            </Box>

            <CommandFooter />
        </CommandDialog>
    );
};
